import { memo, useMemo, useState } from 'react'
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import { EmptyState } from '../common/EmptyState'
import ShortcutRenderer, { getShortcutKeys } from '../help/ShortcutRenderer'
import { SHORTCUT_GROUPS, ShortcutCategory, ShortcutEntry } from '../help/shortcutsData'
import SupportHeader from './SupportHeader'
import SupportScrollArea from './SupportScrollArea'
import SupportSearchField from './SupportSearchField'

type Props = {
    onBack: () => void
}

type Group = { label: ShortcutCategory; rows: ShortcutEntry[] }

const ShortcutRow = ({ entry, last }: { entry: ShortcutEntry; last: boolean }) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        sx={{ py: 0.875, px: 0.5, borderBottom: last ? 0 : 1, borderColor: 'divider' }}
    >
        <Typography variant="body2" sx={{ minWidth: 0, flex: 1, lineHeight: 1.4 }}>
            {entry.title}
        </Typography>
        <ShortcutRenderer shortcutName={entry.shortcutName} compact />
    </Stack>
)

/** Compact, searchable shortcut list grouped by area; only bindings for this OS are shown. */
function ShortcutsViewInner({ onBack }: Props) {
    const [searchText, setSearchText] = useState('')
    const [expanded, setExpanded] = useState<Record<string, boolean>>(() => ({ [SHORTCUT_GROUPS[0]?.label ?? '']: true }))

    // Same shortcut can be listed under two areas; keep it once per group and drop unbound ones.
    const groups = useMemo<Group[]>(
        () =>
            SHORTCUT_GROUPS.map((group) => {
                const seen = new Set<string>()
                const rows = group.shortcuts.filter((entry) => {
                    if (seen.has(entry.shortcutName) || !getShortcutKeys(entry.shortcutName)) return false
                    seen.add(entry.shortcutName)
                    return true
                })
                return { label: group.label, rows }
            }).filter((group) => group.rows.length > 0),
        []
    )

    const query = searchText.trim().toLowerCase()
    const visibleGroups = useMemo<Group[]>(() => {
        if (!query) return groups
        return groups
            .map((group) => ({ label: group.label, rows: group.rows.filter((row) => row.title.toLowerCase().includes(query)) }))
            .filter((group) => group.rows.length > 0)
    }, [groups, query])

    const toggle = (label: string) => setExpanded((prev) => ({ ...prev, [label]: !prev[label] }))

    return (
        <>
            <SupportHeader title="Keyboard shortcuts" onBack={onBack} />
            <SupportScrollArea>
                <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                    <SupportSearchField value={searchText} onChange={setSearchText} placeholder="Search shortcuts" />
                    {visibleGroups.length === 0 ? (
                        <EmptyState text={`No shortcuts match "${searchText.trim()}".`} />
                    ) : (
                        visibleGroups.map((group) => {
                            const open = !!query || !!expanded[group.label]
                            return (
                                <Box key={group.label} sx={{ mt: 1.5 }}>
                                    <ButtonBase
                                        onClick={() => toggle(group.label)}
                                        disabled={!!query}
                                        aria-expanded={open}
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 0.5,
                                            py: 1,
                                            borderRadius: 1,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            textAlign: 'left',
                                            '&:hover': { bgcolor: query ? 'transparent' : 'action.hover' },
                                            '&.Mui-disabled': { color: 'text.primary' }
                                        }}
                                    >
                                        <Stack direction="row" alignItems="baseline" spacing={1}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {group.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.tertiary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {group.rows.length}
                                            </Typography>
                                        </Stack>
                                        {!query ? (
                                            <ThinArrowDownIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: 'text.secondary',
                                                    transform: open ? 'rotate(180deg)' : 'none',
                                                    transition: 'transform 0.15s'
                                                }}
                                            />
                                        ) : null}
                                    </ButtonBase>
                                    <Collapse in={open} unmountOnExit>
                                        <Box>
                                            {group.rows.map((entry, i) => (
                                                <ShortcutRow key={entry.shortcutName} entry={entry} last={i === group.rows.length - 1} />
                                            ))}
                                        </Box>
                                    </Collapse>
                                </Box>
                            )
                        })
                    )}
                </Box>
            </SupportScrollArea>
        </>
    )
}

export default memo(ShortcutsViewInner)
